/**
 * Auth Provider
 *
 * Protects the entire app under authentication.
 * Uses the auth store for state management and token exchange.
 * Renders appropriate screens based on auth state.
 */

import { useEffect, useState, useCallback, ReactNode, useRef } from "react";
import { Platform, View, StyleSheet } from "react-native";
import * as AuthSession from "expo-auth-session";
import * as WebBrowser from "expo-web-browser";
import * as Crypto from "expo-crypto";
import { useRouter, usePathname, useRootNavigationState } from "expo-router";
import { useIsRestoring } from "@tanstack/react-query";
import { useAuth, useTenant } from "@src/features/tenants";
import { authSessionStorage, SESSION_KEYS, isSessionUnlocked, getPostAuthDestination } from "./utils";
import { useAuthConfig } from "./hooks";
import { LoadingScreen, ErrorScreen, SignInScreen } from "./components";
import { useUnistyles } from "react-native-unistyles";
import { secureStorage, SecureStorageKeys } from "@src/services/secure-storage";
import { AppStorageKeys, BiometricPreference, useStorage } from "@src/services/app-storage";
import { useLegacyAuth } from "@src/config/useLegacyAuth";
import { BackgroundGradient } from "@src/components/BackgroundGradient";
import { SystemBars } from "react-native-edge-to-edge";
import { useAmplitude } from "@src/hooks/useAmplitude";
import { ANALYTICS_EVENTS } from "@src/services/analytics/events";
import * as LocalAuthentication from "expo-local-authentication";
import { fetchFlags, useFeatureFlags } from "@src/services/feature-flags/feature-flags";
import { AppNotConfiguredView } from "@src/features/tenants";
import { resolveAuthConnection, type AuthConnection } from "@src/config/auth-connections";
import { triggerAccountLink } from "@src/hooks/account-link";

// Complete auth session for web popup/redirect flows
if (Platform.OS === "web") {
  WebBrowser.maybeCompleteAuthSession();
}

/** Screens that handle their own authentication/onboarding flow */
const AUTH_SCREENS = [
  "/auth/login",
  "/auth/forgot-password",
  "/auth/pin",
  "/auth/confirm-pin",
  "/auth/change-pin",
  "/auth/biometrics",
  "/auth/insert-pin",
  "/auth/biometric-verify",
  "/onboarding/push-notifications",
] as const;

interface AuthProviderProps {
  children: ReactNode;
  shouldShowReactNativeScreens?: boolean | null;
}

export function AuthProvider({ children, shouldShowReactNativeScreens }: AuthProviderProps) {
  const router = useRouter();
  const pathname = usePathname();
  const rootNavigationState = useRootNavigationState();
  const emailRef = useRef("");

  // Get auth state and actions from UserContext
  const {
    user,
    isAuthenticated,
    isInitialized,
    isAuthenticating,
    authError,
    setAuthenticating,
    setAuthError,
    handleTokenExchange,
  } = useAuth();

  // Get tenant state
  const { selectedTenant, isLoading: isLoadingTenants } = useTenant();

  const isRestoringCache = useIsRestoring();

  // Track onboarding completion status
  // On web, onboarding (PIN/biometrics) is always skipped
  const [isOnboardingComplete, setIsOnboardingComplete] = useState<boolean | null>(Platform.OS === "web" ? true : null);
  const [isCheckingOnboarding, setIsCheckingOnboarding] = useState(Platform.OS !== "web");
  const { track } = useAmplitude();

  // Track feature flag fetch status after login
  const [isLoadingFeatureFlags, setIsLoadingFeatureFlags] = useState(false);
  const {
    hasFetchedSuccessfully: featureFlagsReady,
    fetchError: featureFlagsFetchError,
    isLoading: isLoadingFeatureFlagsFromStore,
    getBooleanFlag,
  } = useFeatureFlags();

  // Auth0 configuration from runtime environment
  const authConfig = useAuthConfig();

  // Security tokens for CSRF protection and replay attack prevention
  const [csrfToken] = useState(() => {
    const stored = authSessionStorage.get(SESSION_KEYS.CSRF_TOKEN);
    if (stored) return stored;
    const token = Crypto.randomUUID();
    authSessionStorage.set(SESSION_KEYS.CSRF_TOKEN, token);
    return token;
  });

  const [nonce] = useState(() => {
    const stored = authSessionStorage.get(SESSION_KEYS.NONCE);
    if (stored) return stored;
    const token = Crypto.randomUUID();
    authSessionStorage.set(SESSION_KEYS.NONCE, token);
    return token;
  });

  // The Auth0 connection routed into `extraParams.connection`. Seeded from the
  // optional `?authConnection=` URL override (used by E2E tests + explicit
  // developer overrides) and otherwise driven by the email entered on the
  // Sign In screen — see `handleSignIn` below.
  const [activeConnection, setActiveConnection] = useState<AuthConnection | string | undefined>(
    authConfig.authConnection,
  );

  // Auth request setup with PKCE
  // Note: Fallbacks are for type satisfaction only - when discovery is null, request is null
  const [request, , promptAsync] = AuthSession.useAuthRequest(
    {
      clientId: authConfig?.clientId ?? "",
      scopes: ["openid", "profile", "email", "offline_access"],
      redirectUri: authConfig?.redirectUri ?? "",
      responseType: AuthSession.ResponseType.Code,
      usePKCE: true,
      codeChallengeMethod: AuthSession.CodeChallengeMethod.S256,
      state: JSON.stringify({ csrfToken }),
      extraParams: {
        nonce,
        ...(authConfig?.audience ? { audience: authConfig.audience } : {}),
        ...(activeConnection ? { connection: activeConnection } : {}),
        ...(emailRef.current ? { login_hint: emailRef.current } : {}),
        access_type: "offline",
      },
    },
    authConfig?.discovery ?? null,
  );

  // Store code verifier for web redirect flow (needed after page reload)
  useEffect(() => {
    if (request?.codeVerifier) {
      authSessionStorage.set(SESSION_KEYS.CODE_VERIFIER, request.codeVerifier);
    }
  }, [request?.codeVerifier]);

  // Exchange authorization code for tokens and redirect
  const exchangeCode = useCallback(
    async (code: string, codeVerifier: string) => {
      if (!authConfig) {
        throw new Error("Auth config not loaded");
      }
      try {
        const tokenResponse = await AuthSession.exchangeCodeAsync(
          {
            clientId: authConfig.clientId,
            code,
            redirectUri: authConfig.redirectUri,
            extraParams: { code_verifier: codeVerifier },
          },
          authConfig.discovery,
        );

        authSessionStorage.clearAuthData();
        await handleTokenExchange(tokenResponse);
        await triggerAccountLink(tokenResponse);

        // Track successful Auth0 login completion
        track(ANALYTICS_EVENTS.AUTH0_LOGIN_COMPLETED);

        // Redirect to root - handleRootRedirect will determine the correct destination
        router.replace("/");
      } catch (error) {
        // Track Auth0 login failure
        track(ANALYTICS_EVENTS.AUTH0_LOGIN_FAILED, {
          error_type: error instanceof Error ? error.message : "unknown",
        });
        throw error;
      }
    },
    [authConfig, handleTokenExchange, router, track],
  );

  // Initiate auth flow and handle result directly (not via effect)
  const initiateAuth = useCallback(async () => {
    if (!request) return;

    setAuthenticating(true);
    setAuthError(null);

    // Track Auth0 login start
    track(ANALYTICS_EVENTS.AUTH0_LOGIN_STARTED);

    try {
      const url = request.url ? new URL(request.url) : undefined;
      url?.searchParams.set("login_hint", emailRef.current);
      const result = await promptAsync({ url: url?.toString() });

      if (result.type === "success" && "params" in result && result.params.code) {
        // Validate CSRF token
        const returnedState = result.params.state;
        if (returnedState) {
          const stateData = JSON.parse(returnedState) as { csrfToken?: string };
          if (stateData.csrfToken !== csrfToken) {
            throw new Error("CSRF token mismatch");
          }
        }

        const codeVerifier = request.codeVerifier || authSessionStorage.get(SESSION_KEYS.CODE_VERIFIER);
        if (!codeVerifier) {
          throw new Error("Missing code verifier");
        }

        await exchangeCode(result.params.code, codeVerifier);
      } else if (result.type === "error") {
        const errorMsg = "params" in result ? result.params?.error_description : "Authentication failed";
        throw new Error(errorMsg || "Authentication failed");
      }
    } catch (error) {
      track(ANALYTICS_EVENTS.AUTH0_LOGIN_FAILED, {
        error_type: error instanceof Error ? error.message : "initiation_failed",
      });
      setAuthError(error instanceof Error ? error.message : "Failed to initiate authentication");
    }
  }, [request, promptAsync, csrfToken, exchangeCode, setAuthenticating, setAuthError, track]);

  // Deferred-prompt plumbing for email-driven connection selection.
  //
  // `AuthSession.useAuthRequest` rebuilds its `request` asynchronously when
  // the config changes (e.g. when `activeConnection` flips). We must not call
  // `promptAsync()` until the rebuild has completed, otherwise Auth0 would be
  // hit with the previous connection. We detect "rebuild completed" by
  // watching the `request` reference identity — `useAuthRequest` swaps in a
  // new object only once the new request is fully built.
  const pendingPromptRef = useRef(false);
  const lastSeenRequestRef = useRef<typeof request | null>(null);

  useEffect(() => {
    const requestChanged = request !== lastSeenRequestRef.current;
    lastSeenRequestRef.current = request;

    if (!request || !pendingPromptRef.current || !requestChanged) return;

    pendingPromptRef.current = false;
    void initiateAuth();
  }, [request, initiateAuth]);

  // Submit handler wired to <SignInScreen onSignIn={…}/>. Resolves the Auth0
  // connection from the email domain, with the URL override (?authConnection=)
  // taking precedence when present so E2E tests and explicit developer
  // overrides keep working unchanged.
  const handleSignIn = useCallback(
    (email: string) => {
      emailRef.current = email;
      const next = authConfig.authConnection ?? resolveAuthConnection(email);

      if (next === activeConnection) {
        if (!pendingPromptRef.current) {
          // Request was already built with the right connection — prompt now.
          void initiateAuth();
        }
        // Otherwise a rebuild for this connection is already in flight — the
        // deferred-prompt effect will call initiateAuth once the new request lands.
        return;
      }

      // Trigger a rebuild and prompt once the new `request` lands.
      pendingPromptRef.current = true;
      setActiveConnection(next);
    },
    [activeConnection, authConfig.authConnection, initiateAuth],
  );

  // Retry handler
  const handleRetry = useCallback(() => {
    setAuthError(null);
    initiateAuth();
  }, [initiateAuth, setAuthError]);

  const [biometricPreference, , isLoadingBiometricPreference] = useStorage<BiometricPreference>(
    AppStorageKeys.BIOMETRIC_PREFERENCE,
  );

  // Redirect authenticated users from root to appropriate screen based on onboarding status
  const handleRootRedirect = useCallback(async () => {
    // Avoid navigating before the root layout has mounted.
    if (!rootNavigationState?.key || isLoadingBiometricPreference) {
      return;
    }

    if (isAuthenticated && isInitialized && (pathname === "/" || pathname === "/index")) {
      // Web uses the /index route to redirect to /overview.
      // Avoid native onboarding redirects (PIN/biometrics) on web.
      if (Platform.OS === "web") {
        return;
      }

      // If session is already unlocked (user just completed onboarding or verification), go to overview
      if (isSessionUnlocked()) {
        router.replace(getPostAuthDestination(getBooleanFlag));
        return;
      }

      try {
        const userPin = await secureStorage.get(SecureStorageKeys.userPin);
        const hasMadeChoice = biometricPreference !== null && biometricPreference.biometricEnabled !== undefined;

        // Check if biometrics are actually enrolled and available on this device
        const hasHardware = await LocalAuthentication.hasHardwareAsync();
        const isEnrolled = await LocalAuthentication.isEnrolledAsync();
        const isBiometricAvailable = hasHardware && isEnrolled;

        // PIN must be a non-empty string to be considered valid
        if (userPin && userPin.trim().length > 0) {
          // Returning user with PIN set - verify PIN
          router.replace("/auth/insert-pin");
        } else if (hasMadeChoice && biometricPreference!.biometricEnabled === true && isBiometricAvailable) {
          // Returning user with biometrics enabled AND enrolled - verify biometrics
          router.replace("/auth/biometric-verify");
        } else if (hasMadeChoice) {
          router.replace(getPostAuthDestination(getBooleanFlag));
        } else {
          // First-time user who hasn't made any choices - start onboarding
          router.replace("/auth/biometrics");
        }
      } catch (error) {
        console.error("[AuthProvider] Error checking onboarding for root redirect:", error);
        router.replace(getPostAuthDestination(getBooleanFlag));
      }
    }
  }, [
    isAuthenticated,
    isInitialized,
    pathname,
    router,
    rootNavigationState?.key,
    biometricPreference,
    isLoadingBiometricPreference,
    getBooleanFlag,
  ]);

  // Check onboarding completion status for authenticated users
  const checkOnboardingStatus = useCallback(async () => {
    if (!isInitialized || !isAuthenticated || isLoadingBiometricPreference) {
      setIsCheckingOnboarding(false);
      return;
    }

    // On web, skip onboarding — no PIN/biometrics needed
    if (Platform.OS === "web") {
      setIsOnboardingComplete(true);
      setIsCheckingOnboarding(false);
      return;
    }

    // If session is already unlocked, skip the check - user just completed onboarding/verification
    if (isSessionUnlocked()) {
      setIsOnboardingComplete(true);
      setIsCheckingOnboarding(false);
      return;
    }

    try {
      const userPin = await secureStorage.get(SecureStorageKeys.userPin);
      // PIN must be a non-empty string to be considered valid
      const hasPin = !!(userPin && userPin.trim().length > 0);

      // Check if user has made a biometric choice
      const hasMadeChoice = biometricPreference !== null && biometricPreference.biometricEnabled !== undefined;

      // Onboarding is complete if:
      // 1. PIN is set, OR
      // 2. User has made a biometric choice (enabled or disabled)
      // This allows users who have completed onboarding but disabled their security to access the app
      const isComplete = hasPin || hasMadeChoice;

      setIsOnboardingComplete(isComplete);
    } catch (error) {
      console.error("[AuthProvider] Error checking onboarding status:", error);
      // On error, assume onboarding is NOT complete (safer fallback)
      setIsOnboardingComplete(false);
    } finally {
      setIsCheckingOnboarding(false);
    }
  }, [isAuthenticated, isInitialized, biometricPreference, isLoadingBiometricPreference]);

  // Check onboarding completion and redirect to appropriate screen
  const checkOnboardingProgress = useCallback(async () => {
    // On web, skip onboarding progress checks — no PIN/biometrics needed
    if (Platform.OS === "web") return;

    if (
      isAuthenticated &&
      isInitialized &&
      !isLoadingBiometricPreference &&
      !AUTH_SCREENS.includes(pathname as (typeof AUTH_SCREENS)[number])
    ) {
      try {
        const userPin = await secureStorage.get(SecureStorageKeys.userPin);

        // PIN must be a non-empty string to be considered valid
        if (!userPin || userPin.trim().length === 0) {
          // Check if user has made a biometric choice
          const hasMadeChoice = biometricPreference !== null && biometricPreference.biometricEnabled !== undefined;

          if (!hasMadeChoice) {
            // User hasn't made a choice yet - redirect to biometrics
            router.replace("/auth/biometrics");
          }
          // If user has made a choice (regardless of biometric setting), allow access
          // They've completed onboarding, even if they've disabled their security
        }
        // If PIN is set, user has completed onboarding - allow access to main app
      } catch (error) {
        console.error("[AuthProvider] Error checking onboarding progress:", error);
        // On error, redirect to biometrics (safer than allowing access)
        router.replace("/auth/biometrics");
      }
    }
  }, [isAuthenticated, isInitialized, pathname, router, biometricPreference, isLoadingBiometricPreference]);

  useEffect(() => {
    handleRootRedirect();
  }, [handleRootRedirect]);

  const isLegacyAuth = useLegacyAuth();

  // Whether we have enough information to decide the login screen: either the
  // flag fetch succeeded, or it failed and we've given up waiting. Shared by
  // the redirect effect and getOverlayContent() below so they can't disagree
  // about whether the flag has "settled" — see DEL-513 fix history.
  const canDecideLoginScreen = featureFlagsReady || !!featureFlagsFetchError;

  // Redirect to Django login if not authenticated and not on auth screens
  useEffect(() => {
    if (
      canDecideLoginScreen &&
      isLegacyAuth &&
      isInitialized &&
      !isAuthenticated &&
      !AUTH_SCREENS.includes(pathname as (typeof AUTH_SCREENS)[number])
    ) {
      router.replace("/auth/login");
    }
  }, [isInitialized, isAuthenticated, pathname, router, isLegacyAuth, canDecideLoginScreen]);

  useEffect(() => {
    checkOnboardingStatus();
  }, [checkOnboardingStatus]);

  useEffect(() => {
    checkOnboardingProgress();
  }, [checkOnboardingProgress]);

  // Track user ID to detect when user changes (login/logout/switch)
  const currentUserId = user?.id ?? null;
  const previousUserIdRef = useRef<string | null>(null);

  // Handler for retrying feature flag fetch
  const handleRetryFeatureFlags = useCallback(() => {
    setIsLoadingFeatureFlags(true);
    void fetchFlags().finally(() => {
      setIsLoadingFeatureFlags(false);
    });
  }, []);

  // Fetch feature flags after user becomes authenticated
  // This ensures we have user-specific feature flags before showing the app
  // Re-fetches when user changes (e.g., fresh login after anonymous startup fetch)
  useEffect(() => {
    const userChanged = previousUserIdRef.current !== currentUserId;

    if (
      isAuthenticated &&
      isInitialized &&
      selectedTenant &&
      (!featureFlagsReady || userChanged) &&
      !isLoadingFeatureFlags &&
      !isLoadingFeatureFlagsFromStore &&
      !featureFlagsFetchError
    ) {
      console.log("[AuthProvider] Starting feature flags fetch...");
      previousUserIdRef.current = currentUserId;
      handleRetryFeatureFlags();
    }
  }, [
    isAuthenticated,
    isInitialized,
    selectedTenant,
    currentUserId,
    featureFlagsReady,
    isLoadingFeatureFlags,
    isLoadingFeatureFlagsFromStore,
    featureFlagsFetchError,
    handleRetryFeatureFlags,
  ]);

  const { theme } = useUnistyles();
  const backgroundColor = theme.colors.background as string;

  if (!shouldShowReactNativeScreens) {
    return <View style={{ flex: 1, backgroundColor }} />;
  }

  // Determine overlay content based on auth state
  // On web, we use an overlay pattern to keep the Stack navigator mounted
  // This preserves the navigation state parsed from the URL during initial load
  const getOverlayContent = (): ReactNode | null => {
    if (Platform.OS === "web" && isRestoringCache) {
      return <LoadingScreen message="Loading..." />;
    }

    if (!isInitialized || isAuthenticating) {
      return <LoadingScreen message={isAuthenticating ? "Signing you in..." : "Loading..."} />;
    }

    if (authError && !isAuthenticated) {
      return <ErrorScreen error={authError} onRetry={handleRetry} />;
    }

    // Allow access to auth screens - these handle their own authentication flow
    if (AUTH_SCREENS.includes(pathname as (typeof AUTH_SCREENS)[number])) {
      return null;
    }

    // If not authenticated, show sign in screen (Auth0) or redirect to login (legacy).
    // Wait for the legacy_auth flag to settle before deciding — otherwise we
    // render (and the redirect effect below acts on) whatever the pre-fetch
    // default happens to be instead of the real value.
    if (!isAuthenticated) {
      if (!canDecideLoginScreen) {
        return <LoadingScreen message="Loading..." />;
      }
      if (isLegacyAuth) {
        return <LoadingScreen message="Redirecting to login..." />;
      }
      return (
        <>
          <SystemBars style="light" />
          <SignInScreen onSignIn={handleSignIn} disabled={!request} />
        </>
      );
    }

    // For authenticated users on native, check onboarding completion
    // Web skips onboarding (PIN/biometrics)
    if (Platform.OS !== "web" && isAuthenticated && isInitialized) {
      if (isCheckingOnboarding || isOnboardingComplete === null) {
        return <LoadingScreen message="Checking onboarding status..." />;
      }
      if (!isOnboardingComplete) {
        return <LoadingScreen message="Redirecting to onboarding..." />;
      }
    }

    // Wait for tenant to finish loading (Auth0 flow only)
    if (!isLegacyAuth && isAuthenticated && isLoadingTenants) {
      return <LoadingScreen message="Loading organisation..." />;
    }

    // Show error view if feature flags failed to load
    if (isAuthenticated && featureFlagsFetchError && !featureFlagsReady) {
      console.log("[AuthProvider] Showing AppNotConfiguredView due to error:", featureFlagsFetchError);
      return <AppNotConfiguredView onRetry={handleRetryFeatureFlags} />;
    }

    // Show loading while fetching feature flags
    if (isAuthenticated && selectedTenant && !featureFlagsReady) {
      return <LoadingScreen message="Loading configuration..." />;
    }

    return null;
  };

  const overlayContent = getOverlayContent();

  // Use overlay pattern to keep Stack mounted (preserves URL navigation state)
  const shouldShowOverlay = overlayContent !== null;
  return (
    <BackgroundGradient>
      {/* Children (Stack) always rendered but hidden when overlay is shown */}
      <View style={{ flex: 1, opacity: shouldShowOverlay ? 0 : 1 }}>{children}</View>
      {/* Overlay covers everything when auth/loading states are active */}
      {shouldShowOverlay && <View style={[StyleSheet.absoluteFill, { zIndex: 999 }]}>{overlayContent}</View>}
    </BackgroundGradient>
  );
}
